import * as THREE from 'three';
import type { ExplorationData } from '../labyrinth/ExplorationData';
import { type Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';

const CEIL_SPACING   = 7;
const KERNEL_RADIUS  = 3;                      // tiles — matches the old CEIL_QUAD spread
const KERNEL_SIGMA   = 2.0;                    // tiles — controls falloff smoothness
const LIGHT_COLOR    = new THREE.Color(0xfff4e0);

// Robot headlight — follows the robot, warm amber, larger radius than ceiling
// lamps, recomputed for visibility each frame (LOS from robot's tile).
const ROBOT_KERNEL_RADIUS = 5;
const ROBOT_KERNEL_SIGMA  = 2.8;
const ROBOT_LIGHT_COLOR   = new THREE.Color(0xffd8a8);
const ROBOT_LIGHT_PEAK    = 1.7;

// Wall flashers — rare decorative emergency/indicator lights mounted on wall
// tiles. Small tight splat, sharp strobe envelopes, palette skewed hard to red
// with occasional amber / cyan / green / magenta accents.
const FLASHER_KERNEL_RADIUS = 2;
const FLASHER_KERNEL_SIGMA  = 1.15;
const FLASHER_CHANCE        = 0.025;  // fraction of eligible wall tiles that host a flasher
const FLASHER_PEAK          = 1.55;
// Cumulative-probability palette. Must end with 1.0.
const FLASHER_PALETTE: readonly { p: number; color: THREE.Color }[] = [
  { p: 0.68, color: new THREE.Color(1.00, 0.06, 0.04) }, // red
  { p: 0.82, color: new THREE.Color(1.00, 0.38, 0.06) }, // amber
  { p: 0.90, color: new THREE.Color(0.06, 0.82, 1.00) }, // cyan
  { p: 0.96, color: new THREE.Color(0.18, 1.00, 0.38) }, // green
  { p: 1.00, color: new THREE.Color(1.00, 0.18, 0.82) }, // magenta
];

/** Baseline brightness multiplier for explored-but-unlit surfaces. Unexplored
 *  tiles are hidden by the fog layer so this doesn't need to go to zero. */
export const LIGHTMAP_AMBIENT = new THREE.Color(0.82, 0.82, 0.90);

function lightingHash(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = ((h ^ (h >> 13)) * 1274126177) | 0;
  h = (h ^ (h >> 16)) | 0;
  return (h & 0x7fffffff) / 0x7fffffff;
}

function buildKernel(radius: number, sigma: number): Float32Array {
  const size = radius * 2 + 1;
  const k = new Float32Array(size * size);
  const s2 = 2 * sigma * sigma;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const d2 = dx * dx + dy * dy;
      k[(dy + radius) * size + (dx + radius)] = Math.exp(-d2 / s2);
    }
  }
  return k;
}

/** Integer-step line-of-sight between two tile coords — returns false when a
 *  Wall tile lies strictly between them. Endpoints are never considered
 *  blocking (a wall AT the target is fine; it IS the target). */
function hasLineOfSight(lab: Labyrinth, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const steps = Math.max(Math.abs(dx), Math.abs(dy));
  if (steps <= 1) return true;
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const xi = Math.round(x0 + dx * t);
    const yi = Math.round(y0 + dy * t);
    if (lab.tiles[yi]?.[xi] === Tile.Wall) return false;
  }
  return true;
}

interface Light {
  x: number;
  y: number;
  phase: number;
  isBlinker: boolean;
  /** Kernel weights pre-multiplied by per-tile visibility from this light.
   *  Size = (2*KERNEL_RADIUS+1)². */
  weights: Float32Array;
}

interface Flasher {
  x: number;
  y: number;
  color: THREE.Color;
  period: number;  // seconds
  duty: number;    // fraction of period spent lit
  phase: number;   // seconds
  /** Adjacent floor tiles (dx,dy pairs) — used for fog gating. */
  floorNeighbors: Int8Array;
  /** Visibility-baked kernel weights, size (2*FLASHER_KERNEL_RADIUS+1)². */
  weights: Float32Array;
}

/**
 * Per-tile lightmap consumed by the floor/wall/ceiling shaders.
 *
 * Each ceiling lamp contributes a Gaussian-falloff splat into a DataTexture
 * (one texel per tile, linearly filtered in the shader). Visibility weights
 * are pre-computed per-light so walls block illumination properly. Each frame
 * the accumulator is rebuilt from live flicker/blink values; lamps sitting on
 * unexplored tiles are skipped so fog-of-war stays dark even if the lamp's
 * splat would reach already-explored tiles nearby.
 */
export class Lightmap3D {
  readonly texture: THREE.DataTexture;
  readonly mapSize: THREE.Vector2;

  private readonly lab: Labyrinth;
  private readonly lights: Light[] = [];
  private readonly flashers: Flasher[] = [];
  private readonly width: number;
  private readonly height: number;
  private readonly texData: Uint8Array;
  private readonly accum: Float32Array;
  private readonly robotKernel: Float32Array;

  constructor(lab: Labyrinth) {
    this.lab = lab;
    this.width = lab.width;
    this.height = lab.height;

    const kernel = buildKernel(KERNEL_RADIUS, KERNEL_SIGMA);
    const ksize = KERNEL_RADIUS * 2 + 1;

    // Pre-compute lamp positions (same placement algorithm as the old
    // AmbientLights3D so the lit spots land in the same tiles).
    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        if (lab.tiles[y]![x] === Tile.Wall) continue;

        const gridX = Math.floor(x / CEIL_SPACING);
        const gridY = Math.floor(y / CEIL_SPACING);
        const offsetX = (gridY % 2) * Math.floor(CEIL_SPACING / 2);
        const targetX = gridX * CEIL_SPACING + offsetX + Math.floor(lightingHash(gridX, gridY, 31) * 2);
        const targetY = gridY * CEIL_SPACING + Math.floor(lightingHash(gridX, gridY, 37) * 2);
        if (x !== targetX || y !== targetY) continue;

        let floorNeighbors = 0;
        if (y > 0              && lab.tiles[y - 1]![x] !== Tile.Wall) floorNeighbors++;
        if (y < lab.height - 1 && lab.tiles[y + 1]![x] !== Tile.Wall) floorNeighbors++;
        if (x > 0              && lab.tiles[y]![x - 1] !== Tile.Wall) floorNeighbors++;
        if (x < lab.width - 1  && lab.tiles[y]![x + 1] !== Tile.Wall) floorNeighbors++;
        if (floorNeighbors < 3) continue;

        // Bake visibility into the kernel once — walls block light.
        const weights = new Float32Array(ksize * ksize);
        for (let dy = -KERNEL_RADIUS; dy <= KERNEL_RADIUS; dy++) {
          for (let dx = -KERNEL_RADIUS; dx <= KERNEL_RADIUS; dx++) {
            const ki = (dy + KERNEL_RADIUS) * ksize + (dx + KERNEL_RADIUS);
            const k = kernel[ki]!;
            if (k <= 0) continue;
            const tx = x + dx;
            const ty = y + dy;
            if (tx < 0 || ty < 0 || tx >= this.width || ty >= this.height) continue;
            if (!hasLineOfSight(lab, x, y, tx, ty)) continue;
            weights[ki] = k;
          }
        }

        this.lights.push({
          x, y,
          phase: x * 2.13 + y * 4.67,
          isBlinker: lightingHash(x, y, 11) < 0.15,
          weights,
        });
      }
    }

    // ── Wall flashers ──
    const fKernel = buildKernel(FLASHER_KERNEL_RADIUS, FLASHER_KERNEL_SIGMA);
    const fksize  = FLASHER_KERNEL_RADIUS * 2 + 1;
    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        if (lab.tiles[y]![x] !== Tile.Wall) continue;
        if (lightingHash(x, y, 7) >= FLASHER_CHANCE) continue;

        // Must have at least one adjacent floor — otherwise the light has
        // nothing to fall on that the player will ever see.
        const neigh: number[] = [];
        if (y > 0              && lab.tiles[y - 1]![x] !== Tile.Wall) neigh.push(0, -1);
        if (y < lab.height - 1 && lab.tiles[y + 1]![x] !== Tile.Wall) neigh.push(0,  1);
        if (x > 0              && lab.tiles[y]![x - 1] !== Tile.Wall) neigh.push(-1, 0);
        if (x < lab.width - 1  && lab.tiles[y]![x + 1] !== Tile.Wall) neigh.push( 1, 0);
        if (neigh.length === 0) continue;

        const weights = new Float32Array(fksize * fksize);
        for (let dy = -FLASHER_KERNEL_RADIUS; dy <= FLASHER_KERNEL_RADIUS; dy++) {
          for (let dx = -FLASHER_KERNEL_RADIUS; dx <= FLASHER_KERNEL_RADIUS; dx++) {
            const ki = (dy + FLASHER_KERNEL_RADIUS) * fksize + (dx + FLASHER_KERNEL_RADIUS);
            const k = fKernel[ki]!;
            if (k <= 0) continue;
            const tx = x + dx;
            const ty = y + dy;
            if (tx < 0 || ty < 0 || tx >= this.width || ty >= this.height) continue;
            if (!hasLineOfSight(lab, x, y, tx, ty)) continue;
            weights[ki] = k;
          }
        }

        const hC = lightingHash(x, y, 17);
        let color = FLASHER_PALETTE[0]!.color;
        for (const entry of FLASHER_PALETTE) {
          if (hC < entry.p) { color = entry.color; break; }
        }

        // Period 2–7s, brief lit window (duty 8–18%) → feels like a strobe,
        // not a lamp. Phase spreads flashes across the map so they don't sync.
        const period = 2.0 + lightingHash(x, y, 23) * 5.0;
        const duty   = 0.08 + lightingHash(x, y, 29) * 0.10;
        const phase  = lightingHash(x, y, 41) * period;

        this.flashers.push({
          x, y, color, period, duty, phase,
          floorNeighbors: new Int8Array(neigh),
          weights,
        });
      }
    }

    this.robotKernel = buildKernel(ROBOT_KERNEL_RADIUS, ROBOT_KERNEL_SIGMA);

    const pixels = this.width * this.height;
    this.texData = new Uint8Array(pixels * 4);
    this.accum = new Float32Array(pixels * 3);
    this.texture = new THREE.DataTexture(this.texData, this.width, this.height, THREE.RGBAFormat);
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;

    this.mapSize = new THREE.Vector2(this.width * TILE_SIZE, this.height * TILE_SIZE);
  }

  update(frameTime: number, exploration: ExplorationData, robotTileX: number, robotTileY: number): void {
    const t = frameTime;
    const w = this.width;
    const kr = KERNEL_RADIUS;
    const ksize = kr * 2 + 1;
    const accum = this.accum;
    const lcR = LIGHT_COLOR.r;
    const lcG = LIGHT_COLOR.g;
    const lcB = LIGHT_COLOR.b;

    accum.fill(0);

    for (const L of this.lights) {
      // Fog gate — skip lamps in unexplored tiles. Base/Door tiles are always
      // clear (matches FogMesh3D.isClearTile) so spawn-room lamps stay lit
      // from frame 0.
      const tile = this.lab.tiles[L.y]?.[L.x];
      const alwaysClear = tile === Tile.Base || tile === Tile.Door;
      if (!alwaysClear && !exploration.isRevealed(L.x, L.y)) continue;

      let intensity: number;
      if (L.isBlinker) {
        const bp = L.x * 5.3 + L.y * 2.9;
        const blink = Math.sin(t * 2.5 + bp) * Math.sin(t * 0.7 + bp * 1.3);
        if (blink < -0.3) intensity = 0.05 + (blink + 0.3) * -0.5;
        else              intensity = 0.8 + blink * 0.2;
        if (Math.sin(t * 8.3 + bp * 2.1) > 0.92) intensity *= 0.3;
      } else {
        intensity = 0.9 + Math.sin(t * 0.8 + L.phase) * 0.1;
      }
      if (intensity <= 0) continue;

      const weights = L.weights;
      const y0 = L.y - kr;
      const x0 = L.x - kr;
      for (let ky = 0; ky < ksize; ky++) {
        const yy = y0 + ky;
        if (yy < 0 || yy >= this.height) continue;
        for (let kx = 0; kx < ksize; kx++) {
          const w_ = weights[ky * ksize + kx]! * intensity;
          if (w_ <= 0) continue;
          const xx = x0 + kx;
          if (xx < 0 || xx >= w) continue;
          const idx = (yy * w + xx) * 3;
          accum[idx]!     += w_ * lcR;
          accum[idx + 1]! += w_ * lcG;
          accum[idx + 2]! += w_ * lcB;
        }
      }
    }

    // ── Wall flashers ──
    // Sharp sine-envelope strobes. Skip lamps whose adjacent floor tiles are
    // all still in fog — they'd pop in on reveal otherwise.
    const fkr = FLASHER_KERNEL_RADIUS;
    const fksize = fkr * 2 + 1;
    for (const F of this.flashers) {
      let anyRevealed = false;
      const nb = F.floorNeighbors;
      for (let i = 0; i < nb.length; i += 2) {
        const nx = F.x + nb[i]!;
        const ny = F.y + nb[i + 1]!;
        const tile = this.lab.tiles[ny]?.[nx];
        if (tile === Tile.Base || tile === Tile.Door || exploration.isRevealed(nx, ny)) {
          anyRevealed = true;
          break;
        }
      }
      if (!anyRevealed) continue;

      const cycle = ((t + F.phase) % F.period + F.period) % F.period;
      const litLen = F.period * F.duty;
      if (cycle >= litLen) continue;
      const u = cycle / litLen;                 // 0..1 across the lit window
      const env = Math.sin(u * Math.PI);        // smooth 0→1→0
      const intensity = FLASHER_PEAK * env;
      if (intensity <= 0) continue;

      const cR = F.color.r;
      const cG = F.color.g;
      const cB = F.color.b;
      const weights = F.weights;
      const y0 = F.y - fkr;
      const x0 = F.x - fkr;
      for (let ky = 0; ky < fksize; ky++) {
        const yy = y0 + ky;
        if (yy < 0 || yy >= this.height) continue;
        for (let kx = 0; kx < fksize; kx++) {
          const w_ = weights[ky * fksize + kx]! * intensity;
          if (w_ <= 0) continue;
          const xx = x0 + kx;
          if (xx < 0 || xx >= w) continue;
          const idx = (yy * w + xx) * 3;
          accum[idx]!     += w_ * cR;
          accum[idx + 1]! += w_ * cG;
          accum[idx + 2]! += w_ * cB;
        }
      }
    }

    // ── Robot headlight ──
    // Splats a wider Gaussian at the robot's current tile. Visibility is
    // evaluated per-tile each frame (walls block the glow); the wide kernel +
    // linear texture filter keep motion looking smooth even though the splat
    // centre snaps to integer tiles.
    const rx = Math.round(robotTileX);
    const ry = Math.round(robotTileY);
    if (rx >= 0 && rx < this.width && ry >= 0 && ry < this.height) {
      const rkr = ROBOT_KERNEL_RADIUS;
      const rksize = rkr * 2 + 1;
      const rk = this.robotKernel;
      const rBreathe = ROBOT_LIGHT_PEAK + Math.sin(t * 1.6) * 0.06;
      const rcR = ROBOT_LIGHT_COLOR.r;
      const rcG = ROBOT_LIGHT_COLOR.g;
      const rcB = ROBOT_LIGHT_COLOR.b;

      const ry0 = ry - rkr;
      const rx0 = rx - rkr;
      for (let ky = 0; ky < rksize; ky++) {
        const yy = ry0 + ky;
        if (yy < 0 || yy >= this.height) continue;
        for (let kx = 0; kx < rksize; kx++) {
          const xx = rx0 + kx;
          if (xx < 0 || xx >= w) continue;
          const kw = rk[ky * rksize + kx]! * rBreathe;
          if (kw <= 0) continue;
          if (!hasLineOfSight(this.lab, rx, ry, xx, yy)) continue;
          const idx = (yy * w + xx) * 3;
          accum[idx]!     += kw * rcR;
          accum[idx + 1]! += kw * rcG;
          accum[idx + 2]! += kw * rcB;
        }
      }
    }

    const td = this.texData;
    for (let i = 0, j = 0; i < accum.length; i += 3, j += 4) {
      td[j]     = Math.min(255, accum[i]!     * 255);
      td[j + 1] = Math.min(255, accum[i + 1]! * 255);
      td[j + 2] = Math.min(255, accum[i + 2]! * 255);
      td[j + 3] = 255;
    }
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
